import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { BaseResource } from '../../core/abstractions/base.resource';
import { ApiResponseModel } from '../../models/core/api-response.model';
import { ResourceConfig } from '../../models/core/resource.config';
import { CountryModel } from '../../models/country.model';
import { MetadataModel } from '../../models/metadata.model';
import { SearchItemModel } from '../../models/search-item.model';
import { SimpleItemModel } from '../../models/simple-item.model';

@Injectable({
    providedIn: 'root'
})
export class MetaResource extends BaseResource {
    constructor() {
        super(new ResourceConfig({ baseController: 'meta' }));
    }

    /**
     * Searches the server for the provided term.
     * @param searchTerm Search term
     */
    search(searchTerm: string): Observable<ApiResponseModel<SearchItemModel[]>> {
        return this._requestService.makePost<ApiResponseModel<SearchItemModel[]>>(this.getUrl(`search/${searchTerm}`));
    }

    /**
     * Returns a model containing all metadata for the selected tenant.
     */
    getAll(): Observable<ApiResponseModel<MetadataModel>> {
        return this._requestService.makeGet<ApiResponseModel<MetadataModel>>(this.getUrl(''));
    }

    /**
     * Returns all countries.
     */
    getCountries(): Observable<ApiResponseModel<CountryModel[]>> {
        return this._requestService.makeGet<ApiResponseModel<CountryModel[]>>(this.getUrl('countries'));
    }

    /**
     * Returns the current tenants logo.
     */
    getLogo(): Observable<ApiResponseModel<string>> {
        return this._requestService.makeGet<ApiResponseModel<string>>(this.getUrl('logo'));
    }

    /**
     * Returns all delivery methods.
     */
    getDeliveryMethods(): Observable<ApiResponseModel<SimpleItemModel[]>> {
        return this.getSimpleForAction('delivery-methods');
    }

    /**
     * Returns all salutations.
     */
    getSalutations(): Observable<ApiResponseModel<SimpleItemModel[]>> {
        return this.getSimpleForAction('salutations');
    }

    /**
     * Returns all letter salutations.
     */
    getLetterSalutations(): Observable<ApiResponseModel<SimpleItemModel[]>> {
        return this.getSimpleForAction('letter-salutations');
    }

    /**
     * Returns all salutation's titles.
     */
    getSalutationTitles(): Observable<ApiResponseModel<SimpleItemModel[]>> {
        return this.getSimpleForAction('salutation-titles');
    }

    /**
     * Returns all company's legal forms.
     */
    getCompanyLegalForms(): Observable<ApiResponseModel<SimpleItemModel[]>> {
        return this.getSimpleForAction('company-legal-forms');
    }

    /**
     * Returns all company's activities.
     */
    getCompanyActivities(): Observable<ApiResponseModel<SimpleItemModel[]>> {
        return this.getSimpleForAction('company-activities');
    }

    /**
     * Returns all shareholder's types.
     */
    getShareholderTypes(): Observable<ApiResponseModel<SimpleItemModel[]>> {
        return this.getSimpleForAction('shareholder-types');
    }

    /**
     * Returns all transaction's types.
     */
    getTransactionTypes(): Observable<ApiResponseModel<SimpleItemModel[]>> {
        return this.getSimpleForAction('transaction-types');
    }

    /**
     * Returns selected transaction type.
     */
    getTransactionType(outgoingTypeId: string, incomingTypeId: string): Observable<ApiResponseModel<SimpleItemModel>> {
        return this._requestService.makeGet<ApiResponseModel<SimpleItemModel>>(
            this.getUrl(`transaction-types/${outgoingTypeId}-${incomingTypeId}`)
        );
    }

    /**
     * Returns all ownership's types.
     */
    getOwnershipTypes(): Observable<ApiResponseModel<SimpleItemModel[]>> {
        return this.getSimpleForAction('ownership-types');
    }

    /**
     * Returns all languages.
     */
    getLanguages(): Observable<ApiResponseModel<SimpleItemModel[]>> {
        return this.getSimpleForAction('languages');
    }

    /**
     * Returns all groups of shareholders.
     */
    getShareholderGroups(): Observable<ApiResponseModel<SimpleItemModel[]>> {
        return this.getSimpleForAction('shareholder-groups');
    }

    /**
     * Returns all infomaterials.
     */
    getInfomaterials(): Observable<ApiResponseModel<SimpleItemModel[]>> {
        return this.getSimpleForAction('infomaterial');
    }

    /**
     * Returns all GM statuses.
     */
    getGmStatuses(): Observable<ApiResponseModel<SimpleItemModel[]>> {
        return this.getSimpleForAction('gm-statuses');
    }

    private getSimpleForAction(action: string): Observable<ApiResponseModel<SimpleItemModel[]>> {
        return this._requestService.makeGet<ApiResponseModel<SimpleItemModel[]>>(this.getUrl(action));
    }
}
